import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import * as _moment from 'moment';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { debounceTime, filter } from 'rxjs/operators';
import { CampaignAnalyticsService } from 'src/app/services/campaign-analytics-service';
import { CampaignService } from 'src/app/services/campaign.service';
import { ChannelService } from 'src/app/services/channel.service';
import { buildCampaignDetailsData } from './campaign-numerical-analytics/campaign-numerical-analytics-data.mapper';
import { PdfExportService } from './export-campaign-analytics.service';

const moment = _moment;

@Component({
    selector: 'app-campaign-analytics',
    templateUrl: './campaign-analytics.component.html',
    styleUrls: ['./campaign-analytics.component.scss']
})
export class CampaignAnalyticsComponent implements OnInit {
    public form: FormGroup;
    campaignsWithLiveHistory = null;
    allAvailableChannels = [];
    campaignDetailsInfo = [];
    loading = false;
    loadingDetails = false;
    loadingGraphs = false;
    campaignNumericalMetrics = [];
    campaignGraphicalMetrics = [];
    estimatedFactorsAndSpentOverTime = [];
    factorDistributionAcrossChannels = [];
    liveDateRange: any[];
    minLiveDate;
    maxLiveDate;
    previousStartDate = null;
    previousEndDate = null;

    viewMode: 'Estimated' | 'Actual' = 'Estimated';
    hasActualData: boolean = false;

    /** The selected campaign's id, and the details behind the strip the results dashboard renders. */
    selectedCampaignId: string = null;
    campaignDetails = null;

    /**
     * Whether the external provider is this campaign's metrics source. True only for a CPM campaign
     * while the platform is configured to that source, which is exactly when the provider's own
     * dashboard is the right page to show — the legacy view reports TMC's plays and spend, and the
     * two must not be read as one set of figures.
     */
    externalMetricsSource = false;

    dateFilter = (date: Date | null): boolean => {
        if (!date) return false;
        const day = moment(date);
        return day.isBetween(this.minLiveDate, this.maxLiveDate, 'day', '[]');
    };

    constructor(
        private campaignAnalyticsService: CampaignAnalyticsService,
        private campaignService: CampaignService,
        private channelService: ChannelService,
        private formBuilder: FormBuilder,
        private pdfService: PdfExportService
    ) { }

    ngOnInit(): void {
        this.getCampaignsWithLiveHistory();
        this.initForm();
    }

    get isCampaignCompleted(): boolean {
        const statusInfo = this.campaignDetailsInfo.find(info => info.title === 'Campaign Status');
        const isStatusCompleted = statusInfo?.value === 'COMPLETED';

        return isStatusCompleted;
    }

    /**
     * Whether to show the DOOH Campaign Results dashboard in place of the legacy analytics view.
     * One campaign has one metrics source, so the two pages are alternatives, never both.
     */
    get showResultsDashboard(): boolean {
        return this.externalMetricsSource && this.selectedCampaignId != null;
    }

    get showNoActualDataDisclaimer(): boolean {
        return this.isCampaignCompleted && !this.hasActualData;
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            campaignWithLiveHistory: new FormControl(null),
            channels: new FormControl(null),
            selectedStartDate: new FormControl(null),
            selectedEndDate: new FormControl(null)
        });

        this.form
            .get('campaignWithLiveHistory')
            .valueChanges.pipe(
                debounceTime(300),
                filter(value => typeof value === 'string')
            )
            .subscribe(value => this.getCampaignsWithLiveHistory(value));
    }

    onViewModeChange(mode: 'Estimated' | 'Actual') {
        if (this.viewMode === mode) return;

        this.viewMode = mode;
        this.resetAnalyticsData();

        const formValue = this.form.value;
        if (formValue.campaignWithLiveHistory && formValue.channels && formValue.channels.length > 0) {
            this.previousStartDate = null;
            this.previousEndDate = null;

            this.onDateRangeChange();
        }
    }

    private resetAnalyticsData() {
        this.campaignDetails = null;
        this.campaignDetailsInfo = [];
        this.campaignNumericalMetrics = [];
        this.campaignGraphicalMetrics = [];
        this.estimatedFactorsAndSpentOverTime = [];
        this.factorDistributionAcrossChannels = [];

        this.loading = false;
        this.loadingDetails = false;
        this.loadingGraphs = false;

        this.initializePlaceholders();
        this.campaignGraphicalMetrics = [];
    }

    areAllChannelsSelected(): boolean {
        const pickedChannels = this.form.get('channels').value;

        return this.allAvailableChannels.every((availableChannel: any) =>
            pickedChannels.some(
                (selectedChannel: any) => selectedChannel.idChannel === availableChannel.idChannel
            )
        );
    }

    toggleAllSelection() {
        if (!this.areAllChannelsSelected()) {
            this.form.get('channels').setValue(this.allAvailableChannels);
        } else {
            this.form.get('channels').setValue([]);
        }
        this.onChannelSelectionChange();
    }

    getCampaignsWithLiveHistory(search = null) {
        return this.campaignAnalyticsService
            .getCampaignsWithLiveHistory(search)
            .subscribe((response: any) => {
                this.campaignsWithLiveHistory = response;
            });
    }

    displayFn(campaignWithLiveHistory): string {
        return campaignWithLiveHistory && campaignWithLiveHistory.title
            ? campaignWithLiveHistory.title
            : '';
    }

    onCampaignTitleSelection(campaignWithLiveHistory) {
        this.resetAnalyticsData();

        this.previousStartDate = null;
        this.previousEndDate = null;

        this.selectedCampaignId = campaignWithLiveHistory.campaignId;
        // Which page to show is a property of the campaign, so it is settled before anything is
        // rendered rather than inferred later from whether provider figures happened to arrive.
        this.externalMetricsSource = false;
        this.campaignService
            .getCampaignMetricsById(campaignWithLiveHistory.campaignId)
            .subscribe((metrics: any) => {
                this.externalMetricsSource = !!metrics && !!metrics.externalMetricsSource;
            });

        this.channelService
            .getOnceCampaignLiveChannelsAndDateRange(campaignWithLiveHistory.campaignId)
            .subscribe((response: any) => {
                this.form.get('channels').setValue(response.channels, { emitEvent: false });
                this.allAvailableChannels = response.channels;
                this.updateDateFields(response);
                this.onDateRangeChange();
            });
    }

    onChannelSelectionChange() {
        this.resetAnalyticsData();

        const currentChannels = this.form
            .get('channels')
            .value.filter(channel => channel !== 'All');

        this.form.get('channels').setValue(currentChannels, { emitEvent: false });

        const campaignWithLiveHistory = this.form.get('campaignWithLiveHistory').value;

        if (currentChannels.length != 0) {
            this.previousStartDate = null;
            this.previousEndDate = null;

            this.channelService
                .getCampaignChannelDateRange(campaignWithLiveHistory.campaignId, currentChannels)
                .subscribe((response: any) => {
                    this.updateDateFields(response, true);
                    this.onDateRangeChange();
                });
        }
    }

    removeScheduleChannel(channelIndex: number): void {
        const channelsControl = this.form.get('channels');
        const currentChannels = channelsControl.value as any[];
        const newChannels = currentChannels.filter((_, i) => i !== channelIndex);

        channelsControl.setValue([...newChannels], { emitEvent: false });

        this.onChannelSelectionChange();
    }

    onDatePickerClosed(): void {
        this.onDateRangeChange();
    }

    onDateRangeChange(): void {
        const formValue = this.form.value;
        const startDate = this.form.get('selectedStartDate').value;
        const endDate = this.form.get('selectedEndDate').value;

        if (
            typeof formValue.campaignWithLiveHistory !== 'string' &&
            formValue.campaignWithLiveHistory != null &&
            formValue.channels != null &&
            formValue.channels.length > 0 &&
            startDate != null &&
            endDate != null &&
            startDate !== '' &&
            endDate !== ''
        ) {
            let startDateStr: string;
            let endDateStr: string;

            try {
                startDateStr = moment(startDate).format('YYYY-MM-DD');
                endDateStr = moment(endDate).format('YYYY-MM-DD');

                if (startDateStr === 'Invalid date' || endDateStr === 'Invalid date') {
                    return;
                }
            } catch (error) {
                return;
            }

            if (this.previousStartDate === startDateStr && this.previousEndDate === endDateStr) {
                return;
            }

            this.resetAnalyticsData();

            this.previousStartDate = startDateStr;
            this.previousEndDate = endDateStr;

            this.loading = true;
            this.loadingDetails = true;
            this.loadingGraphs = true;

            this.initializePlaceholders();

            const params = this.buildParamstoBeSent(formValue);

            this.campaignAnalyticsService.getCampaignAnalyticsOverview(params).subscribe(
                (response: any) => {
                    this.loading = false;
                    this.loadingDetails = false;
                    this.loadingGraphs = false;

                    this.hasActualData = response.hasActualData;
                    // Kept unmapped as well: the results dashboard renders its own details strip
                    // from the raw DTO rather than from the legacy page's title/value pairs.
                    this.campaignDetails = response.campaignDetails;

                    if (this.viewMode === 'Estimated' || this.hasActualData) {
                        const detailsData = buildCampaignDetailsData(
                            response.campaignDetails,
                            this.areAllChannelsSelected(),
                            this.viewMode === 'Actual'
                        );
                        this.campaignDetailsInfo = detailsData.campaignDetailsInfo;
                        this.campaignNumericalMetrics = detailsData.campaignNumericalMetrics;

                        this.campaignGraphicalMetrics = response.timeSeriesData.map((est, i) => {
                            const matchingDistribution = response.channelDistribution.find(
                                dist => dist.factorType === est.factorType
                            ) || response.channelDistribution[i];

                            const numericalMetric = this.campaignNumericalMetrics.find(m => m.title === est.factorType);
                            const isDataAvailable = this.viewMode !== 'Actual' || numericalMetric != null;

                            return {
                                estimated: { ...est },
                                distribution: { ...matchingDistribution },
                                isAvailable: isDataAvailable
                            };
                        });
                    }
                },
                (error) => console.error('API Error:', error)
            );
        }
    }

    updateDateFields(response, preserveCustomSelection = false) {
        const dateRange = response.liveDateRange || response;
        const firstDate = dateRange[0];
        const lastDate = dateRange[dateRange.length - 1];

        this.liveDateRange = dateRange;
        this.minLiveDate = firstDate;
        this.maxLiveDate = lastDate;

        const currentStartDate = this.form.get('selectedStartDate').value;
        const currentEndDate = this.form.get('selectedEndDate').value;

        if (preserveCustomSelection && currentStartDate && currentEndDate) {
            const currentStart = moment(currentStartDate).format('YYYY-MM-DD');
            const currentEnd = moment(currentEndDate).format('YYYY-MM-DD');

            if (dateRange.includes(currentStart) && dateRange.includes(currentEnd)) {
                return;
            }
        }

        this.form.get('selectedStartDate').setValue(firstDate, { emitEvent: false });
        this.form.get('selectedEndDate').setValue(lastDate, { emitEvent: false });
    }

    buildParamstoBeSent(formValue) {
        const selectedStartDate = moment(formValue.selectedStartDate).startOf('day');
        const selectedEndDate = moment(formValue.selectedEndDate).endOf('day');

        return {
            isActualMode: this.viewMode === 'Actual',
            campaignWithLiveHistory: formValue.campaignWithLiveHistory,
            channels: formValue.channels,
            liveDateRange: this.liveDateRange.filter(date => {
                const d = moment(date);
                return d.isSameOrAfter(selectedStartDate, 'day') &&
                    d.isSameOrBefore(selectedEndDate, 'day');
            })
        };
    }

    getFilteredCampaignDetails(formValue) {
        const params = this.buildParamstoBeSent(formValue);
        return this.campaignAnalyticsService.getFilteredCampaignDetails(params);
    }

    getEstimatedFactorAndSpentOverTime(formValue) {
        const params = this.buildParamstoBeSent(formValue);
        return this.campaignAnalyticsService.getEstimatedFactorAndSpentOverTime(params);
    }

    getFactorDistributionAcrossChannels(formValue) {
        const params = this.buildParamstoBeSent(formValue);
        return this.campaignAnalyticsService.getFactorDistributionAcrossChannels(params);
    }

    initializePlaceholders() {
        this.campaignDetailsInfo = [
            { title: 'Brand Name', value: '' },
            { title: 'Issuer Name', value: '' },
            { title: 'Pricing Model', value: '' },
            { title: 'Screens', value: '' },
            { title: 'Slots', value: '' }
        ];

        this.campaignNumericalMetrics = [
            {
                title: 'Unspent Balance',
                iconUrl: './assets/images/icons/analytics/unspent-icon.svg'
            },
            { title: 'Spent', iconUrl: './assets/images/icons/analytics/spent-icon.svg' },
            { title: 'Plays', iconUrl: './assets/images/icons/analytics/channels-plays.svg' }
        ];

        this.campaignGraphicalMetrics = [
            {
                estimated: { factorType: 'Plays', dates: [], factorValue: [], spentOverTime: [] },
                distribution: { factorType: 'Plays', channelNames: [], factorValue: [] }
            }
        ];
    }

    async exportToPdf() {
        this.loading = true;
        try {
            await this.pdfService.exportCampaignAnalytics('pdf-export-content', this.form.value);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            this.loading = false;
        }
    }

}
